import { useBooleanState } from '@cian/valuation-utils-component';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FeedContainer } from './FeedContainer';
import { FullscreenViewContainer } from './FullscreenViewContainer';
import { trackEntryIntoView } from './analytics';
import { useHandleOfferRowClick } from './utils/useHandleOfferRowClick';
import { useTitle } from './utils/useTitle';
import { setCategoriesFilter, setIsFiltersChanged } from '../../../actions/offersHistory';
import { selectOffersHistorySpoilerData } from '../../../selectors/offersHistory';
import { OfferHistory, OpenFullscreenBtn } from '../../../components/OffersHistory/Desktop';
import { TRefWrapper } from '../../Scrolling';
import { RefWrapper } from '../../../components/packages/RefWrapper';
import { useActiveCategory } from '../utils';
import { mapOfferCategoriesEnums } from '../../../types/offerHistory';
import { CategoryTabs } from '../../../components/OffersHistory/Desktop/CategoryTabs';

interface IOfferHistoryContainerProps {
  refWrapper: TRefWrapper;
}

export const OfferHistoryContainer: React.FC<IOfferHistoryContainerProps> = ({ refWrapper }) => {
  const dispatch = useDispatch();
  const fullscreenOpenState = useBooleanState(false);
  const [, openFullscreenView] = fullscreenOpenState;
  const { data: offersHistorySpoilerData, isFetching } = useSelector(selectOffersHistorySpoilerData);

  const title = useTitle();
  const [activeCategory, handleActiveCategoryChange] = useActiveCategory();

  const handleOfferClick = useHandleOfferRowClick(openFullscreenView);

  React.useEffect(() => {
    return () => {
      dispatch(setIsFiltersChanged(false));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const handleFullscreenClose = React.useCallback(() => {
    if (offersHistorySpoilerData?.filters?.activeCategory) {
      dispatch(setCategoriesFilter(mapOfferCategoriesEnums[offersHistorySpoilerData.filters.activeCategory]));
    }
  }, [dispatch, offersHistorySpoilerData]);

  if (!offersHistorySpoilerData || !offersHistorySpoilerData.offers.length) {
    return null;
  }

  return refWrapper(
    <RefWrapper>
      <OfferHistory
        title={title}
        tabs={
          offersHistorySpoilerData.filters ? (
            <CategoryTabs
              {...offersHistorySpoilerData.filters}
              activeCategory={activeCategory || offersHistorySpoilerData.filters.activeCategory}
              onTabClick={handleActiveCategoryChange}
            />
          ) : undefined
        }
        offersTableSmall={<FeedContainer onOfferClick={handleOfferClick} isSpoiler />}
        fullscreenViewBtn={
          offersHistorySpoilerData?.totalCountText && !isFetching ? (
            <OpenFullscreenBtn
              onClick={handleOfferClick}
              text={offersHistorySpoilerData.totalCountText}
              onView={trackEntryIntoView}
            />
          ) : undefined
        }
        fullscreenView={
          <FullscreenViewContainer fullscreenOpenState={fullscreenOpenState} onClose={handleFullscreenClose} />
        }
      />
    </RefWrapper>,
  );
};
